import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Minus, Gift, Star } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { LogoMark } from "@/components/Logo";
import { Panel, GoldButton, GhostButton, Select, Empty } from "@/components/common";

export const LoyaltyCard = ({ clientId, clientName }) => {
  const [d, setD] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [rid, setRid] = useState("");
  const load = useCallback(() => api.get(`/clients/${clientId}/loyalty`).then((r) => setD(r.data)), [clientId]);
  useEffect(() => { load(); api.get("/loyalty_rewards").then((r) => setRewards(r.data)); }, [load]);
  if (!d) return null;

  const req = d.stamps_required;
  const full = d.stamps >= req;
  const stamp = async (delta) => { try { const r = await api.post(`/clients/${clientId}/loyalty/stamp`, { delta, note: "" }); setD(r.data); } catch (e) { toast.error(errMsg(e)); } };
  const redeem = async () => {
    if (!rid) return toast.error("Seleziona lo sconto da applicare");
    try { const r = await api.post(`/clients/${clientId}/loyalty/redeem`, { reward_id: rid }); setD(r.data); setRid(""); toast.success("Sconto riscattato"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-5">
      <div className="lg:col-span-3 space-y-4">
        <div className="relative overflow-hidden rounded-2xl bg-[#121212] p-7 text-[#F6F1EA] border border-[#C5A059]" data-testid="loyalty-card">
          <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full border border-[#C5A059]/25" />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3"><LogoMark size={44} /><div><div className="font-serif text-xl">Carta fedeltà</div><div className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">Estetica e Benessere</div></div></div>
            <div className="text-right"><div className="font-serif text-4xl text-[#E9CF86]" data-testid="loyalty-stamps-count">{d.stamps}</div><div className="text-xs text-[#CFC6B8]">su {req}</div></div>
          </div>
          <div className="mt-7 flex flex-wrap gap-2.5">
            {Array.from({ length: req }, (_, i) => (
              <span key={i} className={`flex h-9 w-9 items-center justify-center rounded-full border ${i < d.stamps ? "border-[#C5A059] bg-[#C5A059]/20 text-[#E9CF86]" : "border-[#C5A059]/30 text-[#C5A059]/30"}`}>
                <Star className="h-4 w-4" fill={i < d.stamps ? "currentColor" : "none"} strokeWidth={1.2} />
              </span>
            ))}
          </div>
          <div className="mt-6 text-sm text-[#CFC6B8]">{clientName}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <GhostButton onClick={() => stamp(1)} data-testid="loyalty-add-stamp"><Plus className="h-4 w-4" /> Aggiungi timbro</GhostButton>
          <GhostButton onClick={() => stamp(-1)} disabled={d.stamps === 0} data-testid="loyalty-remove-stamp"><Minus className="h-4 w-4" /> Togli timbro</GhostButton>
        </div>
        <Panel className={`p-5 ${full ? "border-[#C5A059]" : ""}`}>
          <div className="flex items-center gap-2 mb-3"><Gift className="h-4 w-4 text-[#C5A059]" /><h3 className="font-serif text-xl">{full ? "Tessera completa: scegli lo sconto" : `Mancano ${req - d.stamps} timbri per uno sconto`}</h3></div>
          {rewards.length === 0 ? <p className="text-sm text-[#9E958A]">Nessuno sconto configurato. Crealo nella sezione Fedeltà.</p> : (
            <div className="flex flex-wrap gap-2">
              <Select value={rid} onChange={(e) => setRid(e.target.value)} disabled={!full} placeholder="Seleziona sconto" options={rewards.map((r) => ({ value: r.id, label: r.description ? `${r.name} – ${r.description}` : r.name }))} className="max-w-sm" data-testid="loyalty-reward-select" />
              <GoldButton onClick={redeem} disabled={!full || !rid} data-testid="loyalty-redeem-button">Applica sconto</GoldButton>
            </div>
          )}
        </Panel>
      </div>
      <Panel className="p-5 lg:col-span-2">
        <h3 className="font-serif text-xl mb-3">Storico tessera</h3>
        {d.events.length === 0 ? <Empty testId="loyalty-events-empty">Nessun movimento.</Empty> : (
          <ul className="divide-y divide-[#E8E1D7] max-h-[420px] overflow-y-auto">
            {d.events.map((e) => (
              <li key={e.id} className="flex items-center gap-3 py-2.5 text-sm" data-testid={`loyalty-event-${e.id}`}>
                <span className={`w-10 font-serif text-lg ${e.delta > 0 ? "text-[#2D6A4F]" : "text-[#8A6A22]"}`}>{e.delta > 0 ? `+${e.delta}` : e.delta}</span>
                <div className="flex-1 min-w-0">
                  <div className="truncate">{e.type === "riscatto" ? `Sconto: ${e.reward_name}` : e.note || (e.delta > 0 ? "Timbro aggiunto" : "Timbro tolto")}</div>
                  <div className="text-xs text-[#9E958A]">{new Date(e.date).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
};
